import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Loader2, Mail, RefreshCw, CheckCircle2, Sparkles } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function Verification() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    cooldownRef.current = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (code.length === 6 && !loading && !success && email && EMAIL_RE.test(email)) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      toast({ title: 'Email invalide', description: 'Saisissez votre email d\'inscription.' });
      return;
    }
    if (code.length !== 6) {
      toast({ title: 'Code incomplet', description: 'Saisissez les 6 chiffres reçus.' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/verify`, { email: email.trim().toLowerCase(), code });
      setSuccess(true);
      toast({ title: 'Email vérifié !', description: 'Bienvenue dans la ruche.' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Code incorrect ou expiré.';
      toast({ title: 'Vérification échouée', description: typeof msg === 'string' ? msg : 'Erreur.' });
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!EMAIL_RE.test(email)) {
      toast({ title: 'Email invalide', description: 'Saisissez votre email d\'inscription.' });
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API}/resend-verification`, { email: email.trim().toLowerCase() });
      toast({ title: 'Nouveau code envoyé', description: 'Vérifiez votre boîte mail.' });
      setCooldown(60);
      setCode('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Impossible de renvoyer un code pour le moment.';
      toast({ title: 'Erreur', description: typeof msg === 'string' ? msg : 'Erreur.' });
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <section className="min-h-[70vh] flex items-center justify-center px-6 py-20">
        <div className="w-full max-w-md p-10 rounded-2xl border border-yellow-400/30 bg-neutral-900/60 text-center">
          <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-yellow-400/15 text-yellow-400 mb-5">
            <CheckCircle2 size={36} />
          </div>
          <h1 className="text-3xl font-bold text-white">Inscription validée 🐝</h1>
          <p className="mt-3 text-neutral-400">
            Ton compte BuzzBump est maintenant actif. Tu recevras les news du projet par email et tu pourras te connecter à l'app dès sa sortie.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors">
              Retour à l'accueil
            </button>
            <Link to="/notre-projet"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-white/20 text-white hover:border-yellow-400 hover:text-yellow-400 transition-colors">
              Découvrir le projet
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(250,204,21,0.15),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-16 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 text-yellow-300 text-xs font-medium tracking-wider uppercase">
            <ShieldCheck size={14} /> Vérification
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-yellow-400 leading-[1.05]">Saisis ton code</h1>
          <p className="mt-4 text-neutral-300 text-base max-w-xl mx-auto leading-relaxed">
            On vient de t'envoyer un code à 6 chiffres par email. Saisis-le ci-dessous pour valider ton inscription.
          </p>
        </div>
      </section>

      <section className="max-w-md mx-auto px-6 lg:px-10 pb-20">
        <form onSubmit={handleVerify} className="p-7 md:p-9 rounded-2xl border border-white/10 bg-neutral-900/40 space-y-6">
          <div>
            <label className="block text-sm text-neutral-300 mb-2 flex items-center gap-2">
              <Mail size={14} className="text-yellow-400" /> Email
            </label>
            <input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-md bg-neutral-950/80 border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20"
              placeholder="vous@exemple.com" maxLength={120}
              autoComplete="email" disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-300 mb-3 text-center">Code à 6 chiffres</label>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(v) => setCode(v.replace(/\D/g, ''))}
                disabled={loading}
              >
                <InputOTPGroup className="gap-2">
                  {[0,1,2,3,4,5].map((i) => (
                    <InputOTPSlot
                      key={i} index={i}
                      className="h-14 w-12 md:w-14 text-2xl font-bold bg-neutral-950/80 border-white/15 text-yellow-400 data-[active=true]:border-yellow-400/80 data-[active=true]:ring-yellow-400/30 rounded-md"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <p className="mt-3 text-xs text-neutral-500 text-center">Code valable 30 minutes</p>
          </div>

          <button type="submit" disabled={loading || code.length !== 6}
            className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Vérification...</> : <><ShieldCheck size={18} /> Valider mon code</>}
          </button>

          <div className="flex items-center gap-2 pt-2">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-neutral-500">ou</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button type="button" onClick={handleResend} disabled={resending || cooldown > 0 || !email}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-white/15 text-neutral-200 hover:border-yellow-400 hover:text-yellow-400 transition-colors disabled:opacity-50">
            {resending ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {cooldown > 0 ? `Renvoyer dans ${cooldown}s` : 'Renvoyer un nouveau code'}
          </button>

          <p className="text-xs text-neutral-500 text-center">
            Pas encore inscrit ? <Link to="/inscription" className="text-yellow-400 hover:underline">Créer un compte</Link>
          </p>
        </form>
      </section>
    </div>
  );
}
